import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import Seo from "../components/Seo.jsx";
import GoogleLoginButton from "../components/GoogleLoginButton.jsx";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await signup({ name, email, password });
      toast.success("Account created — 20-day free trial started!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brimi-cream flex items-center justify-center px-5">
      <Seo
        title="Start your free trial — BrimiH&G ICSE 2028 Study App"
        description="Create a free BrimiH&G account and get a 20-day trial. AI chapter notes, MCQ practice, tutor chat, mock tests and India + World map quiz for ICSE Class IX-X History, Civics and Geography. No card required."
        path="/signup"
      />
      <div className="w-full max-w-md brimi-card p-8">
        <Link to="/" className="font-heading font-bold text-xl text-brimi-navy block mb-1" data-testid="brand-link">
          Brimi<span className="text-brimi-terracotta">H</span>&<span className="text-brimi-sage">G</span>
        </Link>
        <h1 className="text-2xl font-heading font-semibold text-brimi-navyText mt-3">Start your free trial</h1>
        <p className="text-sm text-brimi-navyText/70 mt-1">
          Twenty days of full access to BrimiH&amp;G — the AI study app for ICSE Class IX-X History,
          Civics and Geography (2028 syllabus). No card required. Cancel anytime.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <GoogleLoginButton label="Sign up with Google" />
          <div className="flex items-center gap-3 my-1">
            <div className="h-px bg-brimi-creamSoft flex-1" />
            <span className="text-xs uppercase tracking-widest text-brimi-navyText/50 font-semibold">or</span>
            <div className="h-px bg-brimi-creamSoft flex-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/70">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
              data-testid="signup-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/70">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
              data-testid="signup-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/70">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
              data-testid="signup-password-input"
            />
          </div>
          <button type="submit" disabled={loading} className="brimi-btn-primary w-full" data-testid="signup-submit-btn">
            {loading ? "Creating account…" : "Create account & start trial"}
          </button>
        </form>
        <p className="text-sm text-brimi-navyText/70 mt-5 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-brimi-navy font-semibold hover:underline" data-testid="goto-login-link">
            Log in
          </Link>
        </p>
        <div className="mt-6 pt-5 border-t border-brimi-creamSoft">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60 mb-2">
            What&apos;s included in every plan
          </p>
          <ul className="text-sm text-brimi-navyText/75 space-y-1.5">
            <li>• 49 chapters covered — History &amp; Civics + Geography for Class IX and X</li>
            <li>• AI chapter notes, PDF download, read-aloud (text-to-speech)</li>
            <li>• Voice-enabled AI tutor chat (Claude Sonnet 4.5)</li>
            <li>• MCQ practice + 20-question timed mock test</li>
            <li>• India &amp; World map work quiz with real geography outlines</li>
            <li>• Installs as an app on your phone or laptop (PWA)</li>
          </ul>
          <p className="text-xs text-brimi-navyText/55 mt-3">
            After the trial: ₹299/month or ₹2,499/year (save ₹1,089). Pay via Razorpay UPI / card / netbanking.
          </p>
        </div>
      </div>
    </div>
  );
}
